import { matchKnownSelector } from "./themeSelectors.js";

// Doc section 6 "Stable element identification" — priority order:
//   1. explicit data-hh-* analytics identifier
//   2. stable Shopify/app identifier (themeSelectors.ts, grounded in the
//      real live theme)
//   3. stable HTML id
//   4. stable semantic attribute
//   5. carefully generated CSS selector
//   6. generic element fallback
//
// Hard rule throughout: never read .value from form controls, never
// include textContent in a generated selector, never touch anything that
// looks like it could carry customer-entered text.

export interface ElementIdentity {
  elementKey: string;
  elementSelector: string;
}

const SENSITIVE_TAGS = new Set(["input", "textarea", "select"]);

function isSensitiveField(el: Element): boolean {
  if (!SENSITIVE_TAGS.has(el.tagName.toLowerCase())) return false;
  const type = (el.getAttribute("type") || "").toLowerCase();
  const name = (el.getAttribute("name") || "").toLowerCase();
  const autocomplete = (el.getAttribute("autocomplete") || "").toLowerCase();
  const sensitiveNamePatterns = ["email", "phone", "tel", "address", "name", "note", "message", "password", "card"];
  if (["email", "password", "tel"].includes(type)) return true;
  if (sensitiveNamePatterns.some((p) => name.includes(p))) return true;
  if (autocomplete && autocomplete !== "off" && sensitiveNamePatterns.some((p) => autocomplete.includes(p))) return true;
  return false;
}

/** True if this element (or an ancestor form) is a free-text customer input we must never fingerprint by content. */
export function isCustomerTextInput(el: Element): boolean {
  if (isSensitiveField(el)) return true;
  const form = el.closest("form");
  if (form && (form.getAttribute("data-hh-track") === "no-track" || form.hasAttribute("data-hide-errors"))) {
    // data-hide-errors is used on the gift-card-recipient form in the live
    // theme (snippets/buy-buttons.liquid) — treat as sensitive by default.
    return true;
  }
  return false;
}

/**
 * Minimal CSS.escape fallback (native CSS.escape is used when available —
 * every real browser has it; this only matters for older/test
 * environments). Escapes characters that are unsafe inside a CSS
 * attribute-selector string.
 */
function cssEscape(value: string): string {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") return CSS.escape(value);
  return value.replace(/["\\]/g, "\\$&");
}

function attr(el: Element, name: string): string | null {
  const v = el.getAttribute(name);
  return v && v.length > 0 && v.length < 200 ? v : null;
}

/** Generates a short, layout-shift-resilient CSS path — structure only, never text content or dynamic values. */
function generateCssPath(el: Element, maxDepth = 5): string {
  const parts: string[] = [];
  let node: Element | null = el;
  let depth = 0;
  while (node && node !== document.body && depth < maxDepth) {
    const currentNode: Element = node;
    const tag = currentNode.tagName.toLowerCase();
    const parent: Element | null = currentNode.parentElement;
    let part = tag;
    if (parent) {
      const siblings: Element[] = Array.from(parent.children).filter((c: Element) => c.tagName === currentNode.tagName);
      if (siblings.length > 1) {
        const index = siblings.indexOf(currentNode) + 1;
        part += `:nth-of-type(${index})`;
      }
    }
    parts.unshift(part);
    node = parent;
    depth++;
  }
  return parts.join(" > ");
}

function genericFallback(el: Element): string {
  const tag = el.tagName.toLowerCase();
  if (tag === "button" || (tag === "input" && attr(el, "type") === "submit")) return "generic_button";
  if (tag === "a") return "generic_link";
  if (tag === "img") return "generic_image";
  if (tag === "select") return "generic_select";
  return `generic_${tag}`;
}

export function resolveElementIdentity(el: Element): ElementIdentity {
  // Priority 1: explicit data-hh-* analytics identifier.
  const explicit = attr(el, "data-hh-track") ?? attr(el, "data-hh-component");
  if (explicit) {
    const section = attr(el, "data-hh-section");
    return { elementKey: section ? `${section}:${explicit}` : explicit, elementSelector: `[data-hh-track="${explicit}"]` };
  }
  // A parent may carry the data-hh-* marker even if the click landed on a child icon/span.
  const markedAncestor = el.closest("[data-hh-track], [data-hh-component]");
  if (markedAncestor && markedAncestor !== el) {
    const markExplicit = attr(markedAncestor, "data-hh-track") ?? attr(markedAncestor, "data-hh-component");
    if (markExplicit) {
      const section = attr(markedAncestor, "data-hh-section");
      return { elementKey: section ? `${section}:${markExplicit}` : markExplicit, elementSelector: `[data-hh-track="${markExplicit}"]` };
    }
  }

  // Priority 2: known, theme-verified Shopify/app identifier.
  const known = matchKnownSelector(el);
  if (known) {
    return { elementKey: known.key, elementSelector: known.selector };
  }

  // Priority 3: stable HTML id (ids in this theme are section/product
  // scoped, e.g. "VariantPicker-{section}-{product}" — stable across
  // reloads of the same page, which is what matters for heatmaps).
  const id = attr(el, "id");
  if (id) {
    return { elementKey: `id:${id.replace(/[0-9]{6,}/g, "#")}`, elementSelector: `#${cssEscape(id)}` };
  }

  // Priority 4: stable semantic attribute.
  const dataOptionSlug = el.closest("[data-option-slug]")?.getAttribute("data-option-slug");
  if (dataOptionSlug) {
    return { elementKey: `option:${dataOptionSlug}`, elementSelector: `[data-option-slug="${dataOptionSlug}"]` };
  }
  const ariaLabel = attr(el, "aria-label");
  if (ariaLabel) {
    return { elementKey: `aria:${ariaLabel.toLowerCase().replace(/\s+/g, "_").slice(0, 60)}`, elementSelector: `[aria-label="${cssEscape(ariaLabel)}"]` };
  }
  const name = attr(el, "name");
  if (name && el.tagName.toLowerCase() !== "input") {
    return { elementKey: `name:${name}`, elementSelector: `[name="${cssEscape(name)}"]` };
  }

  // Priority 5: carefully generated, content-free CSS selector.
  const cssPath = generateCssPath(el);
  if (cssPath) {
    return { elementKey: `path:${cssPath}`, elementSelector: cssPath };
  }

  // Priority 6: generic element fallback.
  const fallback = genericFallback(el);
  return { elementKey: fallback, elementSelector: el.tagName.toLowerCase() };
}

/** Click position relative to the element's own bounding box, 0..1 on each axis (doc section 6). */
export function elementRelativePosition(el: Element, clientX: number, clientY: number): { x: number; y: number } {
  const rect = el.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return { x: 0.5, y: 0.5 };
  const x = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
  const y = Math.min(1, Math.max(0, (clientY - rect.top) / rect.height));
  return { x: Math.round(x * 10000) / 10000, y: Math.round(y * 10000) / 10000 };
}
